import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import yourDocumentPlaceholder from "../../../assets/homepage/yourDocumentPlaceholder.svg";
import yourGraphPlaceholder from "../../../assets/homepage/default_graph.jpg";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  setDocumentID,
  setFloorPlan,
  setFromDocuments,
  setGraphData,
} from "../../../redux/features/graph"; // Adjust the import path as needed
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../axiosInstance";
import { resetNodesState } from "../../../redux/features/nodes";
import { getFloorplanLimits } from "../../outputEditor/utils/helpers";
import { v4 as uuidv4 } from "uuid"; // for generating unique ids

import { Wall } from "../../outputEditor/components/Floorplan"; // adjust path as needed
import { scaleCoordinate } from "../../outputEditor/hooks/useFloorPlan";
import {
  setWalls,
  setRooms,
  setDoorAssets,
  setWindowAssets,
  setLabels,
  resetStates, // Import resetStates
  setHeight,
  setWidth,
} from "../../../redux/features/appState/appState.slice";
import {
  updatePlotHeight,
  updatePlotWidth,
} from "../../../redux/features/nodes";
import { setPlot } from "../../../redux/features/appState/appState.slice";
interface Document {
  id: number;
  name: string;
  date_created: string;
  date_modified: string;
  document_type?: "NEW_INPUT" | "GRAPH" | "FLOORPLAN";
  editor_origin?: string;
  new_input_saved_data?: any;
}

interface ApiResponse {
  documents: Document[];
}

interface DocCardProps {
  id: number;
  title: string;
  date: string;
  imageSrc: string;
  documentType?: string;
  newInputSavedData?: any;
}

function formatISODate(isoDateString: string): string {
  const date = new Date(isoDateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${day}-${month}-${year}, ${hours}:${minutes}:${seconds}`;
}

const hasNonEmptyArray = (value: any) => Array.isArray(value) && value.length > 0;

const hasNonEmptyObject = (value: any) =>
  value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length > 0;

const hasMeaningfulNewInputState = (value: any) => {
  if (!value || typeof value !== "object") return false;

  if (hasNonEmptyArray(value.rooms)) return true;
  if (hasNonEmptyArray(value.fixedRooms)) return true;
  if (hasNonEmptyArray(value.adjacencyPairs)) return true;
  if (hasNonEmptyArray(value.nonAdjacencyPairs)) return true;
  if (hasNonEmptyArray(value.canvasLines)) return true;
  if (hasNonEmptyArray(value.floorplans)) return true;
  if (hasNonEmptyArray(value.apiBoundary)) return true;
  if (hasNonEmptyObject(value.idToNameMap)) return true;
  if (value.spaceResult || value.gaResult) return true;

  return false;
};

type YourDocumentsProps = {
  debouncedQuery: string;
};

export default function YourDocuments({ debouncedQuery }: YourDocumentsProps) {
  const nodes = useSelector((store: RootState) => store?.nodesState?.nodes);
  const links = useSelector((store: RootState) => store.nodesState?.links);
  const fromDocuments = useSelector(
    (store: RootState) => store.graph?.fromDocuments,
  );
  const [floorplanDetails, setFloorplanDetails] = useState<ApiResponse | null>(
    null,
  );
  const [filteredDocs, setFilteredDocs] = useState<ApiResponse | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setFilteredDocs(floorplanDetails); // reset when search is cleared
      return;
    }

    const filtered = floorplanDetails?.documents.filter((doc) =>
      doc.name.toLowerCase().includes(debouncedQuery.toLowerCase()),
    );

    if (filtered === undefined) return;

    setFilteredDocs({ documents: filtered });
  }, [debouncedQuery]);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        console.error("No access token found");
        return;
      }

      const url = `${process.env.REACT_APP_BASEURL}/api/document/`;
      const response = await axiosInstance.get<ApiResponse>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setFloorplanDetails(response.data);
      setFilteredDocs(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  useEffect(() => {
    localStorage.removeItem("lastProcessed");
    fetchDocuments();
  }, []);

  // **DocCard Component Defined Within YourDocuments**
  const DocCard: React.FC<DocCardProps> = ({ id, title, date, imageSrc, documentType, newInputSavedData }) => {
    const [showDelete, setShowDelete] = useState(false);
    const deleteRef: any = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event: any) => {
        if (deleteRef.current && !deleteRef.current.contains(event.target)) {
          setShowDelete(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const handleIconClick = (event: any) => {
      event.stopPropagation();
      setShowDelete(!showDelete);
    };

    const onDelete = (e: any) => {
      e.stopPropagation();

      const token = localStorage.getItem("access_token");
      if (!token) {
        console.error("No access token found");
        return;
      }

      const url = `${process.env.REACT_APP_BASEURL}/api/document/delete/${id}/`;
      axiosInstance
        .post(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response: any) => {
          fetchDocuments();
        })
        .catch((error: any) => {
          console.error("Error:", error);
        });
      setShowDelete(false);
    };

    const handleClick = async (
      docId: number,
      isGraph: boolean,
      fileName: string,
      documentType?: string,
      newInputSavedData?: any,
    ) => {
      try {
        console.log(
          `Clicking on document: ${fileName} (ID: ${docId}, isGraph: ${isGraph}, documentType: ${documentType})`,
        );
        const token = localStorage.getItem("access_token");
        if (!token) {
          console.error("No access token found");
          return;
        }

        // Clear previous graph data from Redux BEFORE fetching new data
        dispatch({ type: "graph/reset" });
        dispatch(resetNodesState());
        localStorage.removeItem("lastProcessed"); // Force re-scaling logic
        const url = `${process.env.REACT_APP_BASEURL}/api/document/${docId}`;
        const response = await axiosInstance.get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log(`Fetched document data for ${fileName}:`, response.data);
        const responseData = response.data || {};
        const resolvedTitle = responseData?.name || fileName;
        const responseDocumentType = responseData?.document_type;
        const responseEditorOrigin = responseData?.editor_origin;
        const responseNewInputSavedData = responseData?.new_input_saved_data;
        const resolvedNewInputSavedData = hasMeaningfulNewInputState(
          responseNewInputSavedData,
        )
          ? responseNewInputSavedData
          : hasMeaningfulNewInputState(newInputSavedData)
            ? newInputSavedData
            : undefined;
        const hasNewInputSavedData = hasMeaningfulNewInputState(
          resolvedNewInputSavedData,
        );
        const floorplanData = responseData?.floorplan_data;
        const hasFloorplanData =
          Boolean(floorplanData) && Object.keys(floorplanData || {}).length > 0;
        const hasGraphData = Boolean(responseData?.graph_saved_data);
        const isGraphDoc = isGraph || resolvedTitle.includes("[GRAPH]");
        // Detect output editor documents (saved with FLOORPLAN type or /outputeditor origin)
        const isOutputEditorDoc =
          responseDocumentType === "FLOORPLAN" ||
          responseEditorOrigin === "/outputeditor";
        // Route to new input editor when explicit markers are present.
        // Rely on document_type / editor_origin (not data-presence heuristics)
        // since the backend may return a default empty graph_saved_data object.
        const isNewInputDoc =
          !isOutputEditorDoc &&
          !hasFloorplanData &&
          (responseDocumentType === "NEW_INPUT" ||
           documentType === "NEW_INPUT" ||
           responseEditorOrigin === "/newinputeditor");

        if (hasFloorplanData) {
          const graph_saved_data = responseData?.graph_saved_data || {};
          const {
            plot_width: backendPlotWidth = window.innerWidth || 1920,
            plot_height: backendPlotHeight = window.innerHeight || 1080,
          } = graph_saved_data || {};

          console.log("Backend Plot Width:", backendPlotWidth);
          console.log("Backend Plot Height:", backendPlotHeight);
          dispatch(updatePlotHeight(backendPlotHeight));
          dispatch(updatePlotWidth(backendPlotWidth));

          // Reset appState before dispatching new floorplan data
          dispatch(resetStates());
          localStorage.setItem(
            "fetched_graph-data_un-normalized_nodes",
            JSON.stringify(graph_saved_data.nodes || []),
          );
          localStorage.setItem(
            "fetched_graph-data_un-normalized_edges",
            JSON.stringify(graph_saved_data.edges || []),
          );
          dispatch(setFromDocuments(true));
          dispatch(setDocumentID(docId));

          const rawWalls = floorplanData.walls || [];
          const rawRooms = floorplanData.rooms || [];
          const plot_boundary: Wall[] = floorplanData.plot_boundary || [];

          // 1) Build walls and back-fill roomIds
          const wallsWithRoomIds = rawWalls.map((w: any) => {
            let roomIds: string[] = [];
            rawRooms.forEach((r: any) => {
              const hasWall = (r.walls || []).some((rw: any) => {
                if (typeof rw === "string" || typeof rw === "number")
                  return rw === w.id;
                if (rw && rw.id) return rw.id === w.id;
                return false;
              });
              if (hasWall) roomIds.push(r.id);
            });
            return { ...w, roomIds };
          });

          // 2) Build rooms with full wall objects
          const roomsWithWallObjects = rawRooms.map((r: any) => {
            const roomWalls = (r.walls || [])
              .map((rw: any) => {
                if (typeof rw === "string" || typeof rw === "number") {
                  return (
                    wallsWithRoomIds.find((w: any) => w.id === rw) ?? null
                  );
                }
                if (rw && rw.id) {
                  return (
                    wallsWithRoomIds.find((w: any) => w.id === rw.id) ?? rw
                  );
                }
                return rw;
              })
              .filter(Boolean);
            return { ...r, walls: roomWalls };
          });

          let scaledPlotBoundary: Wall[] = [];
          plot_boundary?.forEach((wall) => {
            scaledPlotBoundary.push({
              id: wall.id,
              roomIds: wall.roomIds,
              x1: scaleCoordinate(
                wall.x1,
                true,
                backendPlotWidth,
                backendPlotHeight,
              ),
              x2: scaleCoordinate(
                wall.x2,
                true,
                backendPlotWidth,
                backendPlotHeight,
              ),
              y1: scaleCoordinate(
                wall.y1,
                false,
                backendPlotWidth,
                backendPlotHeight,
              ),
              y2: scaleCoordinate(
                wall.y2,
                false,
                backendPlotWidth,
                backendPlotHeight,
              ),
            });
          });

          // 3) Dispatch into Redux
          console.log(scaledPlotBoundary);
          dispatch(setPlot(scaledPlotBoundary));
          dispatch(setWalls(wallsWithRoomIds));
          dispatch(setRooms(roomsWithWallObjects));
          dispatch(setDoorAssets(floorplanData.doors || []));
          dispatch(setWindowAssets(floorplanData.windows || []));
          dispatch(setLabels(floorplanData.labels || []));
          dispatch(setHeight(backendPlotHeight));
          dispatch(setWidth(backendPlotWidth));

          navigate("/outputeditor", {
            state: {
              originPath: "/saved-documents",
              documentId: docId,
              documentTitle: resolvedTitle,
              newInputSavedData: hasNewInputSavedData
                ? resolvedNewInputSavedData
                : undefined,
            },
          });
          return;
        }

        // Fallback: output editor document where the API didn't return floorplan_data.
        // Dispatch graph data and navigate to the output editor so the hooks can regenerate.
        if (isOutputEditorDoc && hasGraphData) {
          const graph_saved_data = responseData?.graph_saved_data || {};
          const {
            plot_width: backendPlotWidth = window.innerWidth || 1920,
            plot_height: backendPlotHeight = window.innerHeight || 1080,
          } = graph_saved_data || {};

          dispatch(updatePlotHeight(backendPlotHeight));
          dispatch(updatePlotWidth(backendPlotWidth));
          dispatch(resetStates());
          localStorage.setItem(
            "fetched_graph-data_un-normalized_nodes",
            JSON.stringify(graph_saved_data.nodes || []),
          );
          localStorage.setItem(
            "fetched_graph-data_un-normalized_edges",
            JSON.stringify(graph_saved_data.edges || []),
          );
          dispatch(setFromDocuments(true));
          dispatch(setDocumentID(docId));
          dispatch(setHeight(backendPlotHeight));
          dispatch(setWidth(backendPlotWidth));

          navigate("/outputeditor", {
            state: {
              originPath: "/saved-documents",
              documentId: docId,
              documentTitle: resolvedTitle,
              newInputSavedData: hasNewInputSavedData
                ? resolvedNewInputSavedData
                : undefined,
            },
          });
          console.log(`Navigated to output editor (fallback) for: ${resolvedTitle}`);
          return;
        }

        // New input editor documents — check BEFORE graph fallbacks
        // so explicit document_type/editor_origin markers take priority.
        if (isNewInputDoc) {
          navigate("/newinputeditor", {
            state: {
              from: "Your Documents",
              documentId: docId,
              documentTitle: resolvedTitle,
              newInputSavedData: resolvedNewInputSavedData,
              timestamp: Date.now(),
            },
            replace: true,
          });
          console.log(`Navigated to new input editor for: ${resolvedTitle}`);
          return;
        }

        if (isGraphDoc && hasGraphData) {
          // For graph documents - direct navigation approach
          if (!responseData.graph_saved_data) {
            console.error("No graph data found in response");
            return;
          }

          // Get saved dimensions from local storage if available
          let savedGraphData = JSON.parse(
            localStorage.getItem("graphDataMap") || "{}",
          );
          const {
            plotWidth: savedPlotWidth = 0,
            plotHeight: savedPlotHeight = 0,
            edges: savedEdges = [],
          } = savedGraphData[fileName] || {};

          const savedData = responseData.graph_saved_data;

          // DON'T set res or dispatch fromDocuments for graphs
          // JUST navigate directly with clear unique state
          navigate("/inputeditor", {
            state: {
              from: "Your Documents",
              documentId: docId,
              documentTitle: resolvedTitle.replace(" [GRAPH]", ""),
              graphData: savedData,
              nodes: savedData.nodes || [],
              edges: savedEdges.length > 0 ? savedEdges : savedData.edges || [],
              plotWidth:
                savedPlotWidth || savedData.plot_width || window.innerWidth,
              plotHeight:
                savedPlotHeight || savedData.plot_height || window.innerHeight,
              floorplans: responseData.floorplans || [],
              timestamp: Date.now(), // Ensure uniqueness
            },
            replace: true, // Replace history entry to prevent back-button issues
          });
          console.log(`Navigated to input editor for graph: ${resolvedTitle}`);
          return;
        }

        if (hasGraphData) {
          // Fallback for graph data without a [GRAPH] suffix
          const savedData = responseData.graph_saved_data;
          navigate("/inputeditor", {
            state: {
              from: "Your Documents",
              documentId: docId,
              documentTitle: resolvedTitle.replace(" [GRAPH]", ""),
              graphData: savedData,
              nodes: savedData.nodes || [],
              edges: savedData.edges || [],
              plotWidth: savedData.plot_width || window.innerWidth,
              plotHeight: savedData.plot_height || window.innerHeight,
              floorplans: responseData.floorplans || [],
              timestamp: Date.now(),
            },
            replace: true,
          });
          return;
        }

        console.warn("No supported document data found to open.");
      } catch (error) {
        console.error("Error fetching document details:", error);
      }
    };

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
        <Box>
          <Box
            width="100%"
            onClick={() => {
              handleClick(id, title.includes("[GRAPH]"), title, documentType, newInputSavedData);
            }}
            sx={{
              backgroundColor: "white",
              "&:hover": {
                cursor: "pointer",
                transform: "translate(-2px, -2px)",
                // boxShadow: "6px 6px 0px #1a1a1a" 
              },
              "&:active": {
                transform: "translate(1px, 1px)",
                // boxShadow: "1px 1px 0px #1a1a1a"
              },
              borderRadius: 3,
              border: "2px solid #1a1a1a",
              // boxShadow: "4px 4px 0px #1a1a1a",
              p: 2,
              transition: "all 0.1s ease",
            }}
          >
            <img
              src={imageSrc}
              height="150px"
              width="100%"
              style={{ objectFit: "cover", borderRadius: "8px" }}
              alt={title}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 1,
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  mr: "16px",
                  color: "#FF9A56",
                  textAlign: "left",
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: 600,
                  lineHeight: "normal",
                  mb: "3px",
                  mt: "16px",
                }}
              >
                {title}
              </Typography>
              <Box
                ref={deleteRef}
                position="relative"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "fit-content", marginTop: "10px" }}
                zIndex={10000}
              >
                <MoreHorizIcon
                  htmlColor="#FF9A56"
                  fontSize="small"
                  onClick={handleIconClick}
                  style={{ cursor: "pointer" }}
                />

                {showDelete && (
                  <Button
                    variant="outlined"
                    onClick={onDelete}
                    sx={{
                      position: "absolute",
                      top: "14px", // Position it next to the icon
                      backgroundColor: "#FBE5C6",
                      borderRadius: 1.5,
                      border: "1px solid #e0e0e0",
                      color: "#000",
                      padding: "4px 8px",
                      "&:hover": {
                        backgroundColor: "#FBE5C6",
                      },
                    }}
                  >
                    Delete
                  </Button>
                )}
              </Box>
            </Box>
            <Typography
              sx={{
                mr: "10px",
                color: "#7794B4",
                textAlign: "left",
                fontFamily: "Poppins",
                fontSize: "12px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "15px",
              }}
            >
              {date}
            </Typography>
          </Box>
        </Box>
      </Grid>
    );
  };

  return (
    <Grid
      container
      sx={{
        backgroundColor: "#FBE5C6",
        pl: "7%",
        pr: "7%",
        pb: "32px",
        mb: "36px",
        mt: "16px",
      }}
    >
      <Box sx={{ width: "100%" }}>
        <Grid container spacing={4}>
          {filteredDocs &&
            filteredDocs.documents.map((doc) => (
              <DocCard
                key={doc.id}
                id={doc.id}
                title={doc.name}
                date={formatISODate(doc.date_modified)}
                documentType={doc.document_type}
                newInputSavedData={doc.new_input_saved_data}
                imageSrc={
                  doc.document_type === "NEW_INPUT"
                    ? yourDocumentPlaceholder
                    : doc.name.includes("[GRAPH]")
                      ? yourGraphPlaceholder
                      : yourDocumentPlaceholder
                }
              />
            ))}
        </Grid>
      </Box>
    </Grid>
  );
}
